import React from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { getCurrencySign } from '@zonofi/common/functions/format-amount';
import { i18nService } from '@zonofi/i18n';
import './upload-invoice-form.scss';
import { commonInputSx } from '../../helper';

const mutilineSx = {
  '& .MuiOutlinedInput-root': {
    height: '125px',
    alignItems: 'flex-start',
    borderRadius: '8px',
    '& fieldset': {
      borderWidth: 1.25,
      borderColor: '#8C8C8C',
    },
    '&.Mui-focused fieldset': {
      borderWidth: 1.25,
      borderColor: '#8C8C8C',
    },
  },
  '& .MuiOutlinedInput-input': {
    height: '100%',
    fontSize: '16px',
    fontFamily: 'Segoe-UI-VF',
    color: '#000000',
    fontWeight: 500,
    '&::placeholder': {
      color: '#BFBFBF',
      opacity: 1,
      fontSize: '15px',
    },
  },
};

export const invoice_details = [
  {
    key: 'orderNumbers',
    label: 'Order Numbers',
    type: 'dropdown',
    required: false,
    placeHolder: 'Select Order Numbers',
    emptyMessage: 'No orders found for this customer',
  },
  {
    key: 'invoiceNumber',
    label: 'Invoice Number',
    required: true,
    placeHolder: 'Enter Invoice Number',
  },
  {
    key: 'invoiceAmount',
    label: 'Invoice Amount',
    required: true,
    prefix: getCurrencySign(),
    placeHolder: 'Enter Amount',
  },
  {
    key: 'invoiceDate',
    label: 'Invoice Date',
    type: 'date',
    required: true,
    placeHolder: 'DD/MM/YYYY',
  },
  {
    key: 'dueDate',
    label: 'Due Date',
    type: 'date',
    required: true,
    placeHolder: 'DD/MM/YYYY',
  },
  {
    key: 'note',
    label: 'Note',
    required: false,
    placeHolder: 'Enter Message...',
    mutiline: true,
  },
];

interface UploadInvoiceFormProps {
  formData: Record<string, any>;
  handleChange: (key: string, value: any) => void;
  orderDropdownOptions: { label: string; value: string }[];
  disableOrderDropdown?: boolean;
}

export const UploadInvoiceForm: React.FC<UploadInvoiceFormProps> = ({
  formData,
  handleChange,
  orderDropdownOptions,
  disableOrderDropdown = false,
}) => {
  return (
    <>
      {invoice_details.map((field, index) => {
        const label = `${field.label}${field.required ? '*' : ''}`;

        if (field.type === 'dropdown') {
          return (
            <TextField
              key={`${field.key}-${index}`}
              select
              label={label}
              value={(formData[field.key] ?? []) as string[]}
              onChange={(e) => handleChange(field.key, e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              sx={commonInputSx}
              InputLabelProps={{ shrink: true }}
              disabled={disableOrderDropdown}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: (selected) => {
                  const values = selected as string[];
                  if (!values.length) {
                    return (
                      <span className="upload-message-text font-medium">
                        {field.placeHolder}
                      </span>
                    );
                  }

                  return orderDropdownOptions
                    .filter((opt) => values.includes(opt.value))
                    .map((opt) => opt.label)
                    .join(', ');
                },
              }}
            >
              {orderDropdownOptions?.length === 0 ? (
                <MenuItem disabled value="">
                  <span className="upload-message-text empty-message-color font-medium">
                    {field.emptyMessage}
                  </span>
                </MenuItem>
              ) : (
                orderDropdownOptions.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    value={opt.value}
                    sx={{
                      fontSize: '14px',
                      fontFamily: 'Segoe-UI-VF',
                      '&.Mui-selected': {
                        backgroundColor: '#132C45',
                        color: '#FFFFFF',
                      },
                      '&.Mui-selected:hover': {
                        backgroundColor: '#132C45',
                      },
                    }}
                  >
                    {opt.label}
                  </MenuItem>
                ))
              )}
            </TextField>
          );
        }

        if (field.type === 'date') {
          return (
            <LocalizationProvider
              key={`${field.key}-${index}`}
              dateAdapter={AdapterDayjs}
            >
              <DatePicker
                label={label}
                format="DD/MM/YYYY"
                value={
                  formData[field.key]
                    ? dayjs(formData[field.key] as string, 'DD/MM/YYYY')
                    : null
                }
                onChange={(date) =>
                  handleChange(field.key, date ? date.format('DD/MM/YYYY') : '')
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    variant: 'outlined',
                    sx: commonInputSx,
                    InputLabelProps: { shrink: true },
                    inputProps: {
                      placeholder: field.placeHolder,
                    },
                  },
                }}
              />
            </LocalizationProvider>
          );
        }

        return (
          <TextField
            key={`${field.key}-${index}`}
            label={label}
            value={formData[field.key] ?? ''}
            onChange={(e) => handleChange(field.key, e.target.value)}
            fullWidth
            margin="normal"
            variant="outlined"
            sx={{
              ...commonInputSx,
              ...(field.mutiline && mutilineSx),
            }}
            placeholder={field.placeHolder}
            multiline={field.mutiline}
            rows={field.mutiline ? 4 : undefined}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: field.label?.includes('Amount') ? (
                <div className="invoice-amount-view">
                  <span className="invoice-amount-view-text font-regular">
                    {i18nService.getCurrencySymbol()}
                  </span>
                  <span className="invoice-amount-view-divider-view" />
                </div>
              ) : null,
            }}
          />
        );
      })}
    </>
  );
};
